import { useContext } from "react";
import { SocketContext } from "@contexts/SocketContext";
import IDeleteOrderEntryItemDTO from "@models/DTO/IDeleteOrderEntryItemDTO";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import IOrder from "@models/IOrder";
import IOrder_Item from "@models/IOrder_Item";
import EndpointsEnum from "@models/EndpointsEnum";
import IUpdateOrderEntryItemDTO from "@models/DTO/IUpdateOrderEntryItemDTO";
import IUpdateOrderEntryDetailDTO from "@models/DTO/IUpdateOrderEntryDetailDTO";
import ISubmitOrderDTO from "@models/DTO/ISubmitOrderDTO";
import VendorContext from "@contexts/VendorContext";
import { ProcessOrderEntryItemFn } from "@models/OrderEntryTypes";
import ICreateOrderEntryItemDTO from "@models/DTO/ICreateOrderEntryItemDTO";
import PriceUpchargeTypeEnum from "@models/PriceUpchargeTypeEnum";
import OrderEntryService from "./OrderEntryService";

type AddOrderEntryItemFn = (
    order: IOrder,
    rowData: Parameters<ProcessOrderEntryItemFn>[0],
    options?: Parameters<ProcessOrderEntryItemFn>[1]
) => void;

type UpdateOrderEntryItemFn = (
    order: IOrder,
    rowData: Parameters<ProcessOrderEntryItemFn>[0],
    options?: Parameters<ProcessOrderEntryItemFn>[1]
) => void;

export const OrderEntryApiService = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;

    const deleteOrderEntryItem = (order: IOrder, orderEntryItem: IOrder_Item) => {
        const payload: IDeleteOrderEntryItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: order.guid,
            orderEntryItemGuid: orderEntryItem.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.OrderEntryDeleteOrderEntryItem, payload);
    };

    const updateOrderEntryItem: UpdateOrderEntryItemFn = (order, rowData, options) => {
        const payload: Partial<IUpdateOrderEntryItemDTO> = {
            ...rowData!,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: order.guid,
            orderEntryItemGuid: rowData.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryItem, payload);
    };

    const updateOrderEntryDetails = (order: IOrder, data: Partial<IUpdateOrderEntryDetailDTO>) => {
        let vendorGuid = vendorModel.guid;

        if (userModel.isSupplierUser) {
            if (order.Vendor) {
                vendorGuid = order.Vendor.guid;
            }
        }

        const payload: IUpdateOrderEntryDetailDTO = {
            ...data!,
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            orderGuid: order.guid,
            vendorGuid: vendorGuid,
        };

        socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);
    };

    const submitOrder = (order: IOrder, data: Partial<ISubmitOrderDTO>) => {
        const payload: ISubmitOrderDTO = {
            ...data,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: order.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.OrderEntrySubmitOrder, payload);
    };

    const addOrderEntryItem: AddOrderEntryItemFn = (order, rowData, options) => {
        const payload: ICreateOrderEntryItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: order.guid,
            catalog_item_id: rowData.id || (rowData as any).catalog_item_id,
            item_no: options?.itemNo || 1,
            qty: (rowData as any).qty || 1,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        // Add optional properties if they exist
        if (options?.kitDetails) {
            payload.kitDetails = options.kitDetails;
        }
        if (options?.kitBuilderOptions) {
            payload.kitBuilderData = options.kitBuilderOptions;
        }
        if (options?.itemOptions) {
            payload.itemOptionValues = options.itemOptions.map((option) => ({
                item_option_id: option.item_option_id!,
                value: option.value,
            }));
        }

        socket.emit(EndpointsEnum.OrderEntryCreateOrderEntryItem, payload);
    };

    return {
        deleteOrderEntryItem,
        updateOrderEntryItem,
        updateOrderEntryDetails,
        submitOrder,
        addOrderEntryItem,
    };
};
